import React, { useState } from "react";
import { Copy, Trash2, MessageSquare } from "lucide-react";
import { Button } from "../../../../components/ui/button";
import { Switch } from "../../../../components/ui/switch";
import { Input } from "../../../../components/ui/input";
import {
	FIELD_TYPE_SUPPORTS_PLACEHOLDER,
	FieldType,
} from "../../types/formTypes";
import { TooltipWrapper } from "../../../../components/ui/tooltip-wrapper";

interface FieldFooterProps {
	fieldType: FieldType;
	required: boolean;
	description?: string;
	placeholder?: string;
	multi?: boolean;
	onUpdate: (updates: {
		required?: boolean;
		description?: string;
		placeholder?: string;
		multi?: boolean;
	}) => void;
	onDuplicate: () => void;
	onDelete: () => void;
}

const FieldFooter: React.FC<FieldFooterProps> = ({
	fieldType,
	required,
	description,
	placeholder,
	multi,
	onUpdate,
	onDuplicate,
	onDelete,
}) => {
	const [showDescription, setShowDescription] = useState(
		!!description ||
			(FIELD_TYPE_SUPPORTS_PLACEHOLDER(fieldType) && !!placeholder)
	);

	return (
		<div className="space-y-3">
			<div className="flex items-center justify-between">
				<div className="flex items-center gap-2">
					<TooltipWrapper title="Feld duplizieren" duration={150}>
						<Button
							variant="ghost"
							size="icon"
							onClick={onDuplicate}
							className="h-8 w-8"
							aria-label="Feld duplizieren"
							data-testid="duplicate-field-button"
						>
							<Copy className="h-4 w-4" />
						</Button>
					</TooltipWrapper>

					<TooltipWrapper title="Feld löschen" duration={150}>
						<Button
							variant="ghost"
							size="icon"
							onClick={onDelete}
							className="h-8 w-8"
							aria-label="Feld löschen"
							data-testid="delete-field-button"
						>
							<Trash2 className="h-4 w-4" />
						</Button>
					</TooltipWrapper>
				</div>

				<div className="flex items-center gap-4">
					<Button
						variant="ghost"
						size="sm"
						onClick={() => setShowDescription(!showDescription)}
						className={`h-8 gap-2 ${
							showDescription ? "bg-gray-100" : ""
						}`}
						aria-label="Beschreibung"
						data-testid="toggle-description-button"
					>
						<MessageSquare className="h-4 w-4" />
						<span className="text-sm">Beschreibung</span>
					</Button>

					{fieldType === "chips" && (
						<div className="flex items-center gap-2">
							<span className="text-sm text-gray-700">
								Mehrfachauswahl
							</span>
							<Switch
								checked={!!multi}
								onCheckedChange={(checked) =>
									onUpdate({ multi: checked })
								}
								aria-label="Mehrfachauswahl"
								data-testid="multi-toggle"
							/>
						</div>
					)}

					<div className="flex items-center gap-2">
						<span className="text-sm text-gray-700">
							Erforderlich
						</span>
						<Switch
							checked={required}
							onCheckedChange={(checked) =>
								onUpdate({ required: checked })
							}
							aria-label="Erforderlich"
							data-testid="required-toggle"
						/>
					</div>
				</div>
			</div>

			{showDescription && (
				<div className="space-y-2">
					<Input
						value={description || ""}
						onChange={(e) =>
							onUpdate({ description: e.target.value })
						}
						placeholder="Zusätzliche Beschreibung (optional)"
						className="text-sm"
						aria-label="Feldbeschreibung"
						data-testid="description-input"
					/>
					{FIELD_TYPE_SUPPORTS_PLACEHOLDER(fieldType) && (
						<Input
							value={placeholder || ""}
							onChange={(e) =>
								onUpdate({ placeholder: e.target.value })
							}
							placeholder="Platzhaltertext"
							className="text-sm"
							aria-label="Platzhalter"
							data-testid="placeholder-input"
						/>
					)}
				</div>
			)}
		</div>
	);
};

export default FieldFooter;
